import React, { useEffect, useState } from "react";

/* The day pickers are SelectMenu rather than native <select>s — F-105. */
import { SelectMenu } from "./u1/SelectMenu";

/* The four helpers below are exported so the u1 working-hours fields can reuse them rather than restate them. */
export const days = [
  { label: "Mon", value: "Mon" },
  { label: "Tue", value: "Tue" },
  { label: "Wed", value: "Wed" },
  { label: "Thu", value: "Thu" },
  { label: "Fri", value: "Fri" },
  { label: "Sat", value: "Sat" },
  { label: "Sun", value: "Sun" },
];

export const dayMap = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

export const reverseDayMap = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

// ✅ Parse string → form
export const parseWorkingHours = (str) => {
  if (!str) return null;

  try {
    const regex =
      /(\w+)\s+to\s+(\w+)\s+(\d{1,2})(?::(\d{2}))?(AM|PM)\s+to\s+(\d{1,2})(?::(\d{2}))?(AM|PM)/i;

    const match = str.match(regex);
    if (!match) return null;

    const [
      ,
      fromDay,
      toDay,
      openHour,
      openMin = "00",
      openPeriod,
      closeHour,
      closeMin = "00",
      closePeriod,
    ] = match;

    const convert = (h, m, period) => {
      let hour = parseInt(h, 10);

      if (period.toUpperCase() === "PM" && hour !== 12) hour += 12;
      if (period.toUpperCase() === "AM" && hour === 12) hour = 0;

      return `${String(hour).padStart(2, "0")}:${m}`;
    };

    return {
      fromDay: reverseDayMap[fromDay] || "Mon",
      toDay: reverseDayMap[toDay] || "Fri",
      openTime: convert(openHour, openMin, openPeriod),
      closeTime: convert(closeHour, closeMin, closePeriod),
    };
  } catch {
    return null;
  }
};

// ✅ Convert form → string
export const formatWorkingHours = (form) => {
  const { fromDay, toDay, openTime, closeTime } = form;

  const convert = (time) => {
    let [h, m] = time.split(":");
    h = parseInt(h, 10);

    const suffix = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;

    return m === "00" ? `${hour}${suffix}` : `${hour}:${m}${suffix}`;
  };

  return `${dayMap[fromDay]} to ${dayMap[toDay]} ${convert(
    openTime
  )} to ${convert(closeTime)}`;
};

export default function WorkingHoursUI({
  selectedTime = "",
  setSelectedTime,
  error,
}) {

  const [form, setForm] = useState(() => {
    const defaultValues = {
      fromDay: "Mon",
      toDay: "Fri",
      openTime: "09:00",
      closeTime: "18:00",
    };

    if (selectedTime) {
      const parsed = parseWorkingHours(selectedTime);
      if (parsed) return parsed;
    }

    return defaultValues;
  });

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // ✅ Update parent string
  useEffect(() => {
    if (form.openTime && form.closeTime) {
      setSelectedTime(formatWorkingHours(form));
    }
  }, [form]);

  return (
    <div className="max-w-2xl mx-auto mt-1 font-sans">
      <h3 className="mb-1 text-primary text-f14 font-semibold text-left">
        Working Hours
      </h3>

      <div className="bg-gray-100 rounded-2xl p-6 grid grid-cols-2 gap-2 shadow-sm">

        {/* FROM DAY */}
        <div className="flex flex-col gap-2">
          <label className="text-f12 text-gray-500 font-medium text-left">
            FROM DAY
          </label>
          <SelectMenu
            value={form.fromDay}
            onChange={(e) => handleChange("fromDay", e.target.value)}
            options={days}
          />
        </div>

        {/* TO DAY */}
        <div className="flex flex-col gap-2">
          <label className="text-f12 text-gray-500 font-medium text-left">
            TO DAY
          </label>
          <SelectMenu
            value={form.toDay}
            onChange={(e) => handleChange("toDay", e.target.value)}
            options={days}
          />
        </div>

        {/* OPEN TIME */}
        <div className="flex flex-col gap-2">
          <label className="text-f12 text-gray-500 font-medium text-left">
            OPEN
          </label>
          <input
            type="time"
            value={form.openTime}
            onChange={(e) => handleChange("openTime", e.target.value)}
            className="px-4 py-3 rounded-2xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* CLOSE TIME */}
        <div className="flex flex-col gap-2">
          <label className="text-f12 text-gray-500 font-medium text-left">
            CLOSE
          </label>
          <input
            type="time"
            value={form.closeTime}
            onChange={(e) => handleChange("closeTime", e.target.value)}
            className="px-4 py-3 rounded-2xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
      </div>

      {/* Preview */}
      <div className="mt-5 text-gray-700">
        <span className="font-semibold">Preview:</span>{" "}
        {formatWorkingHours(form)}
      </div>

      {error && <p className="text-red-500 text-sm text-left">{error}</p>}
    </div>
  );
}